import type { TextFieldProps } from '@mui/material';
import { Box, InputAdornment, TextField, useTheme } from '@mui/material';
import { ModalActions } from 'components/Modal/ModalActions';
import { SendMessageButton } from 'components/TextInput/SendMessageButton';
import { MESSAGE_PADDING } from 'embed/constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import React from 'react';
import { selectLegibleButtonOutlineColor } from 'reducers/config';
import { Tooltip } from '@forethought-technologies/forethought-elements';
import { selectIsWidgetVisible, selectWidgetStyle } from 'reducers/main';
import {
  selectShouldFocusTextInput,
  setShouldFocusTextInput,
} from 'reducers/workflows';

type TextInputProps = TextFieldProps & {
  alwaysFocusedBorder?: boolean;
  disableSendMessage: boolean;
  EndAdornment?: JSX.Element;
  onSendMessage: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  sendButtonTooltipContent?: string;
  showActions?: boolean;
  visuallyHiddenSendButton?: boolean;
};

export const TextInput = ({
  alwaysFocusedBorder = false,
  disableSendMessage,
  EndAdornment,
  onSendMessage,
  sendButtonTooltipContent,
  showActions = true,
  visuallyHiddenSendButton = false,
  ...props
}: TextInputProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const legibleButtonOutlineColor = useAppSelector(
    selectLegibleButtonOutlineColor,
  );
  const isWidgetVisible = useAppSelector(selectIsWidgetVisible);
  const widgetStyle = useAppSelector(selectWidgetStyle);
  const shouldFocusTextInput = useAppSelector(selectShouldFocusTextInput);
  const inputRef = React.useRef<HTMLInputElement>();
  const focusedBorder = `1px solid ${legibleButtonOutlineColor}`;
  const border = alwaysFocusedBorder
    ? focusedBorder
    : `1px solid ${theme.palette.colors.slate[200]}`;
  const showInputActions =
    isWidgetVisible && widgetStyle === 'floating_bar' && showActions;

  // handle focus on text input
  React.useEffect(() => {
    if (shouldFocusTextInput && inputRef.current) {
      inputRef.current.focus();

      // Place cursor at the end of any existing text
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);

      // Reset the focus state
      dispatch(setShouldFocusTextInput(false));
    }
  }, [dispatch, shouldFocusTextInput]);

  const DefaultEndAdornment = React.useMemo(() => {
    return (
      <InputAdornment
        className={visuallyHiddenSendButton ? 'visually-hidden' : ''}
        position='end'
      >
        <Tooltip
          placement='top'
          tooltipContent={sendButtonTooltipContent || ''}
        >
          <SendMessageButton
            aria-disabled={disableSendMessage}
            aria-label='Send message'
            data-testid='formWrapper-submit-button'
            onClick={event => {
              if (disableSendMessage) {
                event.preventDefault();
                return;
              }
              onSendMessage(event);
            }}
            type='button'
          />
        </Tooltip>
      </InputAdornment>
    );
  }, [
    disableSendMessage,
    onSendMessage,
    sendButtonTooltipContent,
    visuallyHiddenSendButton,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: '8px',
        width: '100%',
      }}
    >
      <TextField
        {...props}
        autoComplete='off'
        fullWidth
        InputProps={{
          ...props.InputProps,
          endAdornment: EndAdornment ? EndAdornment : DefaultEndAdornment,
        }}
        inputRef={inputRef}
        onChange={e => {
          if (e.target.value !== '\n') {
            props.onChange?.(e);
          }
        }}
        sx={{
          ...props.sx,
          '& .MuiInputBase-input': {
            padding: !props.multiline ? '0' : undefined,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset, &:hover fieldset': {
              borderBottom: border,
              borderLeft: border,
              borderRight: border,
              borderTop: border,
            },
            '&.Mui-focused fieldset': {
              borderBottom: focusedBorder,
              borderLeft: focusedBorder,
              borderRight: focusedBorder,
              borderTop: focusedBorder,
            },
            borderRadius: '43px',
            fontFamily: 'inherit',
            padding: MESSAGE_PADDING,
            width: '100%',
          },
        }}
      />
      {showInputActions && (
        <Box sx={{ alignItems: 'center', display: 'flex' }}>
          <ModalActions color={theme.palette.colors.grey[700]} size={24} />
        </Box>
      )}
    </Box>
  );
};
